/**
 * Sign in.
 *
 * Follows 8loop's auth layout (research/design-system.md §3.5) — muted ground,
 * two columns, the form in a `max-w-md` card — with the marketing panel replaced by
 * the one thing an FDE landing here needs to know: what this app does to their
 * tenant, and that it will not dial outside the hours they set.
 */

import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CalendarClock, PhoneOutgoing, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { errorMessage } from "@/lib/api";
import { useLogin, useMe } from "@/lib/queries";

const FEATURES = [
  {
    icon: PhoneOutgoing,
    title: "Callbacks dial themselves",
    body: "A post-call hook on each agent posts the number and the time it captured. This app places the click-to-call when that time comes, with the context from the first conversation attached.",
  },
  {
    icon: CalendarClock,
    title: "Only inside your calling hours",
    body: "Someone who asks for 10pm on a Sunday is queued for the first open hour instead — and every retry obeys the same windows.",
  },
  {
    icon: ShieldCheck,
    title: "Retries that stop",
    body: "A voicemail that keeps going to voicemail is capped per number per agent channel, so nobody gets rung forever.",
  },
];

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me } = useMe();
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const from = (location.state as { from?: string } | null)?.from ?? "/accounts";

  useEffect(() => {
    document.title = "Sign in · Callbacks";
  }, []);

  if (me?.user) return <Navigate to={from} replace />;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password }, { onSuccess: () => navigate(from, { replace: true }) });
  };

  return (
    <div className="flex min-h-screen w-full items-center bg-muted/40 dark:bg-neutral-900">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="hidden lg:block">
            <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
              8loop · internal
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">
              Callbacks, without the export and re-upload.
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-[52ch]">
              One place for every account you look after: who asked to be called back, when, and
              which agent channel will ring them.
            </p>
            <ul className="mt-10 space-y-6">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border bg-muted">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">{f.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground max-w-[52ch]">{f.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center lg:justify-end">
            <Card className="w-full max-w-md rounded-2xl shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl">Sign in</CardTitle>
                <CardDescription>
                  Use your 8loop email. Your account list is whatever you have been given access
                  to — ask an admin if one is missing.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@8loop.ai"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  {login.isError && (
                    <Alert variant="destructive">
                      <AlertDescription>{errorMessage(login.error)}</AlertDescription>
                    </Alert>
                  )}

                  <Button type="submit" className="h-10 w-full text-base" disabled={login.isPending}>
                    {login.isPending ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
